import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { notification } from './messaging';
import { useFlags, useFlagsmith } from 'flagsmith/react';
import {
  useGetFisheryAdminUserQuery,
  useGetUserQuery,
  useIsAnglerLoggedInQuery,
  useTriggerPasswordResetEmailMutation,
} from '../components/user_profile/userProfileSlice';
import { apiSlice } from '../store/apiSlice';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useValidateOneClickMutation } from '../components/AnglingTrust/one_click/oneClickSlice';
import { PaymentProvider } from '../components/user_bookings/components/payment_page/PaymentPage';
import { preloadImage } from './imagePreloader';
import { hasCookieConsent } from './analytics';

const fullWidthAbsoluteElementIds = ['fixed-header'];

/**
 * Prevents background page (body) scroll. E.g. when a modal/drawer is open
 * @param controllerKey a unique key used to identify which elements are currently disabling the scroll
 * @param isScrollDisabled whether scroll is currently disabled
 */
export function usePreventScroll(controllerKey, isScrollDisabled) {
  const dataPropertyName = 'scrollController';

  const getControllerKeys = useCallback(
    () => JSON.parse(document.body.dataset?.[dataPropertyName] || null) || [],
    []
  );

  /**
   * Is the element corresponding to controllerKey currently locking scroll?
   */
  const isControllingScroll = useCallback(
    () => getControllerKeys()?.includes(controllerKey),
    [controllerKey, getControllerKeys]
  );

  /**
   * Register a scroll lock associated with controllerKey
   */
  const lockScroll = useCallback(() => {
    const scrollBarWidth = Math.max(
      window.innerWidth - document.body.clientWidth,
      0
    );
    document.body.style.overflow = 'hidden';
    fullWidthAbsoluteElementIds.forEach((id) => {
      if (document?.getElementById(id)) {
        document.getElementById(id).style.width =
          `calc(100vw - ${scrollBarWidth}px)`;
      }
    });
    document.body.style.width = `calc(100vw - ${scrollBarWidth}px)`;
    if (!isControllingScroll()) {
      document.body.dataset[dataPropertyName] = JSON.stringify(
        getControllerKeys().concat([controllerKey])
      );
    }
  }, [controllerKey, getControllerKeys, isControllingScroll]);

  /**
   * Unregister a scroll lock associated with controllerKey
   */
  const unlockScroll = useCallback(() => {
    if (isControllingScroll()) {
      document.body.dataset[dataPropertyName] = JSON.stringify(
        getControllerKeys().filter((key) => key !== controllerKey)
      );
    }
    if (!getControllerKeys()?.length) {
      // No controlling elements -- unset scroll
      document.body.style.removeProperty('overflow');
      document.body.style.width = `auto`;
      if (document?.getElementById('fixed-header')) {
        document.getElementById('fixed-header').style.width = `100%`;
      }
      fullWidthAbsoluteElementIds.forEach((id) => {
        if (document?.getElementById(id)) {
          document.getElementById(id).style.width = `100%`;
        }
      });
      // document.body.style.paddingRight = "0";
    }
  }, [controllerKey, getControllerKeys, isControllingScroll]);

  useEffect(() => {
    if (!document) {
      return;
    }
    if (isScrollDisabled) {
      lockScroll();
    } else {
      unlockScroll();
    }

    return () => document && unlockScroll();
  }, [isScrollDisabled, lockScroll, unlockScroll]);
}

export const useFormSubmitCheck = (
  form,
  required_fields,
  block_on_errors = false
) => {
  const [disableSubmit, setDisableSubmit] = useState(true);

  const handleFieldsChange = useCallback(() => {
    const allTouched =
      required_fields
        .map((fName) => form.isFieldTouched(fName) || form.getFieldValue(fName))
        .filter((touched) => touched).length >= required_fields.length;
    const anyErrors = block_on_errors
      ? form.getFieldsError().filter(({ errors }) => errors.length).length > 0
      : false;
    const enableSubmit = allTouched && !anyErrors;
    setDisableSubmit(!enableSubmit);
  }, [block_on_errors, form, required_fields]);

  return [handleFieldsChange, disableSubmit];
};

export const UserIdentificationProvider = ({ children }) => {
  useUserIdentification();
  return children;
};

export const useUserIdentification = (isAdminUser = false) => {
  const flagsmith = useFlagsmith();
  const { data: anglerInfo } = useGetUserQuery();
  const { data: fisheryAdminInfo } = useGetFisheryAdminUserQuery('', {
    skip: !isAdminUser,
  });

  // Update heap properties whenever angler details changes
  useEffect(() => {
    let user = fisheryAdminInfo ?? anglerInfo;
    if (!user) {
      return;
    }
    const is_fishery_admin = Boolean(fisheryAdminInfo || user?.fishery_id);
    user = is_fishery_admin ? fisheryAdminInfo : anglerInfo;
    const tracking_id = is_fishery_admin
      ? 'Fishery ' + user.fishery_id
      : user.email;

    if (flagsmith) {
      flagsmith.identify(tracking_id, {
        app_public_id: user?.public_id ?? user?.id,
        is_fishery_admin: is_fishery_admin,
        fishery_id: user?.fishery_id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
      });
    }

    if (!hasCookieConsent()) {
      return;
    }
    if (window?.heap) {
      window.heap.identify(tracking_id);
      window.heap.addUserProperties({
        app_public_id: user?.public_id ?? user?.id,
        is_fishery_admin: is_fishery_admin,
        fishery_id: user?.fishery_id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
      });
    }
  }, [flagsmith, anglerInfo, fisheryAdminInfo]);

  const resetIdentity = useCallback(() => {
    window?.heap?.resetIdentity();
    flagsmith?.logout();
  }, [flagsmith]);

  return { resetIdentity };
};

export function useLocalStorage(key, initialValue) {
  const [localState, setLocalState] = useState(() => {
    try {
      const value = window.localStorage.getItem(key);
      if (value) {
        return value;
      } else {
        window.localStorage.setItem(key, JSON.stringify(initialValue));
        window.dispatchEvent(new Event('storage'));
        return JSON.stringify(initialValue);
      }
    } catch (error) {
      window.localStorage.setItem(key, JSON.stringify(initialValue));
      window.dispatchEvent(new Event('storage'));
      return JSON.stringify(initialValue);
    }
  });

  const setState = useCallback(
    (v) => {
      try {
        const nextState =
          typeof v === 'function' ? v(JSON.parse(localState)) : v;

        if (nextState === undefined || nextState === null) {
          window.localStorage.removeItem(key);
          setLocalState(null);
        } else {
          window.localStorage.setItem(key, JSON.stringify(nextState));
          setLocalState(JSON.stringify(nextState));
          window.dispatchEvent(new Event('storage'));
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, localState]
  );

  useEffect(() => {
    if (
      window.localStorage.getItem(key) === null &&
      typeof initialValue !== 'undefined'
    ) {
      window.localStorage.setItem(key, JSON.stringify(initialValue));
      setLocalState(JSON.stringify(initialValue));
      window.dispatchEvent(new Event('storage'));
    }
  }, [key, initialValue]);

  useEffect(() => {
    // Triggers an update in the calling component if a different component updates the state value
    const updateLocalState = () => {
      setLocalState(window.localStorage.getItem(key));
    };
    window.addEventListener('storage', updateLocalState);

    return () => window.removeEventListener('storage', updateLocalState);
  }, [key]);

  return [localState ? JSON.parse(localState) : initialValue, setState];
}

export const useLogin = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const instance = useMemo(
    () =>
      axios.create({
        withCredentials: true,
        crossDomain: true,
        baseURL: `${process.env.REACT_APP_AXIOS_URL}`,
        'Access-Control-Allow-Origin': '*',
        sameSite: null,
        secure: false,
      }),
    []
  );

  const onLogin = (
    { username = null, password = null },
    showSuccessNotification = true
  ) => {
    if (!username || !password) {
      notification.error({
        description: 'Please provide your email and password',
      });
    }

    const formData = new FormData();
    formData.append('username', username.trim());
    formData.append('password', password);

    const config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };
    setLoading(true);
    return instance
      .post('login', formData, config)
      .then((res) => {
        if (res.status === 200) {
          let data = res.data.result;
          if (showSuccessNotification) {
            notification.success({
              description: data,
            });
          }
        } else {
          notification.error({
            description: 'Incorrect Details. Please try again.',
          });
          return Promise.reject({ response: res });
        }
      })
      .catch((err) => {
        notification.error({
          description: 'Incorrect Details. Please try again.',
        });
        if (err.response) {
          if (err.response.status !== 401 && err.response.status !== 200) {
            notification.error({
              description: 'Incorrect Details. Please try again.',
            });
          }
        }
        return Promise.reject(err);
      })
      .finally(() => {
        dispatch(
          apiSlice.util.invalidateTags([
            'FisheryUser',
            'User',
            'Subscription',
            'PrintSettings',
          ])
        );
        setLoading(false);
      });
  };

  const refreshToken = useCallback(() => {
    instance
      .get('refresh')
      .then(() => {})
      .catch(() => {})
      .finally(() => {});
  }, [instance]);

  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  return [onLogin, loading];
};

export const useResetPassword = (verificationId, mode = 'user_reset') => {
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [validating, setValidating] = useState(Boolean(verificationId));
  const [submitting, setSubmitting] = useState(false);
  const [linkExpired, setLinkExpired] = useState(null);

  const resetPassword = useCallback(
    (newPassword, showSuccessNotification = true) => {
      if (!newPassword) {
        notification.error({
          description: 'Please provide your new password',
        });
      }
      setSubmitting(true);
      const payload_data = {
        token: verificationId,
        mode: mode,
        password: newPassword,
      };
      const axios_config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      return axios
        .post('fishery/auth/reset', payload_data, axios_config)
        .then((res) => {
          if (res.status === 200) {
            let data = res.data.result;
            if (showSuccessNotification) {
              notification.success({
                description: data,
              });
            }
          }
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status !== 401 && err.response.status !== 200) {
              notification.error({
                description: err.response.data.detail,
              });
            }
          }
          return Promise.reject(err);
        })
        .finally(() => setSubmitting(false));
    },
    [mode, verificationId]
  );

  const validateLink = useCallback((verificationId, mode) => {
    if (!verificationId || !mode) {
      setIsTokenValid(false);
      return;
    }
    setValidating(true);
    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      params: {
        token: verificationId,
        mode: mode,
      },
    };

    setLinkExpired(null);
    axios
      .get('fishery/auth/verify', axios_config)
      .then((res) => {
        if (res.status === 200) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
      })
      .catch((err) => {
        setIsTokenValid(false);
        if (err?.response?.status === 410) {
          setLinkExpired(true);
        }
      })
      .finally(() => setValidating(false));
  }, []);

  useEffect(() => {
    validateLink(verificationId, mode);
  }, [mode, validateLink, verificationId]);

  return [
    isTokenValid,
    resetPassword,
    {
      isValidating: validating,
      isSubmitting: submitting,
      linkExpired: linkExpired,
    },
  ];
};

export const useAccountCreation = ({ email, token, alreadyActive = false }) => {
  const [login, loggingIn] = useLogin();
  const [
    isTokenValid,
    resetPassword,
    {
      isValidating: validatingToken,
      isSubmitting: submittingReset,
      linkExpired,
    },
  ] = useResetPassword(token);
  const [triggerPasswordReset, { isLoading: triggeringReset }] =
    useTriggerPasswordResetEmailMutation();
  const [resetTriggered, setResetTriggered] = useState(false);

  const [submissionError, setSubmissionError] = useState(null);
  const isSubmitting = submittingReset || loggingIn;
  const isLoading = validatingToken || triggeringReset;

  useEffect(() => {
    if (validatingToken || resetTriggered || !email || alreadyActive) {
      return;
    }
    if (!isTokenValid && linkExpired) {
      // Trigger a new token link to be emailed to the user
      triggerPasswordReset({ email: email })
        .unwrap()
        .then(() => setResetTriggered(true));
    }
  }, [
    email,
    isTokenValid,
    linkExpired,
    resetTriggered,
    triggerPasswordReset,
    validatingToken,
    alreadyActive,
  ]);

  const resetPasswordAndLogin = useCallback(
    (email, password) => {
      setSubmissionError(null);
      return resetPassword(password, false)
        .then(() =>
          login(
            {
              username: email,
              password: password,
            },
            false
          )
        )
        .then(() =>
          notification.success({
            description:
              'Your account has been created successfully and you’ve been logged in.',
          })
        )
        .catch((err) => setSubmissionError(err));
    },
    [login, resetPassword]
  );

  return [
    resetPasswordAndLogin,
    {
      isLoading,
      isSubmitting,
      isTokenValid,
      isResetTriggered: resetTriggered,
      submissionError,
    },
  ];
};

export function useOneClickRedirect(verificationId) {
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [validating, setValidating] = useState(Boolean(verificationId));
  const [redirecting, setRedirecting] = useState(false);
  const [linkExpired, setLinkExpired] = useState(null);

  const [validateOneClickLink] = useValidateOneClickMutation();

  useEffect(() => {
    setLinkExpired(null);
    if (!verificationId) {
      setValidating(false);
      setIsTokenValid(false);
      return;
    }
    setValidating(true);
    validateOneClickLink(verificationId)
      .unwrap()
      .then((body) => {
        if (body?.redirect_url) {
          setRedirecting(true);
          window.location.href = body.redirect_url;
        }
        setIsTokenValid(true);
      })
      .catch((err) => {
        setIsTokenValid(false);
        if (err?.response?.status === 410) {
          setLinkExpired(true);
        }
      })
      .finally(() => {
        setValidating(false);
      });
  }, [validateOneClickLink, verificationId]);

  return [
    isTokenValid,
    {
      isValidating: validating,
      isRedirecting: redirecting,
      linkExpired: linkExpired,
    },
  ];
}

export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const useCheckAdminInWebview = () => {
  const isWebview = useQuery().get('isWebview');
  const [inWebview, setInWebview] = useLocalStorage('adminIsInWebview', false);
  useEffect(() => {
    if (isWebview && !inWebview) {
      setInWebview(true);
    }
  }, [inWebview, isWebview, setInWebview]);
};

const TRUSTPILOT_SCRIPT_URL =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

/**
 * Renders a Trustpilot TrustBox into `ref` once `isEnabled`, loading
 * Trustpilot's bootstrap script the first time a TrustBox needs it.
 */
export const useTrustpilotWidget = (ref, isEnabled = true) => {
  useEffect(() => {
    const element = ref?.current;
    if (!isEnabled || !element) {
      return undefined;
    }

    const loadWidget = () => window.Trustpilot?.loadFromElement(element, true);
    if (window.Trustpilot) {
      loadWidget();
      return undefined;
    }

    let script = document.querySelector(
      `script[src="${TRUSTPILOT_SCRIPT_URL}"]`
    );
    if (!script) {
      script = document.createElement('script');
      script.src = TRUSTPILOT_SCRIPT_URL;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', loadWidget);
    return () => script.removeEventListener('load', loadWidget);
  }, [ref, isEnabled]);
};

/**
 * Parses feature flag configurations to determine which payment provider to use for a given fishery and payment type.
 *
 * Uses ryft_payments and stripe_payments feature flags. If either has .all set to true in its value, then that one is
 * treated as the *default*, and the other is treated as an override (preferred, e.g. if the fishery ID matches).
 * If both have .all set to true, Stripe is preferred and always used.
 *
 * If .all is false, for that flag to "match", the transaction's passed `fisheryId` has to be present in the
 * `.booking_fishery_ids` field (where the ID is available, e.g. ignored for event payments) and the passed `paymentType`
 * needs to be present in the `.async_payment_types`field (unless the passed payment type is null or ==
 * 'OnlineBookingPayment'). If either field is empty/not present in the flag value, it is ignored
 * (assumed as a valid match) to allow for Example configuration 1 (below) to match all payment types (for example),
 * whereas if both contain values, they must both match (Example configuration 2 (below)).
 *
 * Ideally *at least one* (and also ideally *only one*) of the flags should have `value.all = true` as this will ensure
 * that all transactions have a valid payment provider, (and it will be clearer which flag is acting as the override
 * case if only one of them has `.all = true`).
 *
 * ### Example configuration 1
 * All payment types payments for fisheries 10101 and 10102 will use Stripe, all other transactions will use Ryft
 * ```json
 * stripe_payments.value = {
 *   "all": false,
 *   "booking_fishery_ids": [10101, 10102],
 *   "async_payment_types": []
 * }
 * ryft_payments.value = {
 *   "all": true,
 *   "booking_fishery_ids": [],
 *   "async_payment_types": []
 * }
 * ```
 *
 * ### Example configuration 2
 * Balance payments for fisheries 10101 and 10102 will use Ryft, all other transactions will use Stripe
 * ```json
 * stripe_payments.value = {
 *   "all": true,
 *   "booking_fishery_ids": [],
 *   "async_payment_types": []
 * }
 * ryft_payments.value = {
 *   "all": false,
 *   "booking_fishery_ids": [10101, 10102],
 *   "async_payment_types": ["AdminBookingBalance", "UserBookingBalance"]
 * }
 * ```
 *
 * ### Example configuration 3
 * All payments will use Stripe as this is the default defined for this case (both have `.all=true`)
 * ```json
 * stripe_payments.value = {
 *   "all": true,
 *   "booking_fishery_ids": [],
 *   "async_payment_types": []
 * }
 * ryft_payments.value = {
 *   "all": true,
 *   "booking_fishery_ids": [],
 *   "async_payment_types": []
 * }
 * ```
 *
 * @returns {{paymentProvider: string, applePayEnabled: boolean, storedCardsEnabled: boolean}}
 */
export function usePaymentProviderFlags({
  fisheryPublicId,
  loadingFisheryId = false,
  paymentType = 'OnlineBookingPayment',
}) {
  const {
    stripe_payments: { enabled: stripeEnabled, value: stripeValueStr },
    ryft_payments: { enabled: ryftEnabled, value: ryftValueStr = 'null' },
    ryft_apple_pay: { enabled: ryftAplePayEnabled },
    ryft_stored_cards: { enabled: ryftStoredCardsEnabled },
  } = useFlags([
    'stripe_payments',
    'ryft_payments',
    'ryft_apple_pay',
    'ryft_stored_cards',
  ]);
  const { data: isLoggedIn } = useIsAnglerLoggedInQuery();

  const stripeFlag = stripeEnabled
    ? JSON.parse(stripeValueStr || 'null')
    : null;
  const ryftFlag = ryftEnabled ? JSON.parse(ryftValueStr || 'null') : null;

  const needsFisheryId =
    stripeFlag?.booking_fishery_ids?.length > 0 ||
    ryftFlag?.booking_fishery_ids?.length > 0; // Any flag has a fishery-id-based override setup
  const isLoading = loadingFisheryId && needsFisheryId;

  const matchesProvider = (provider) => {
    if (!provider) return false;

    if (provider?.all) return true;

    let matchesType = true;
    let matchesFishery = true;
    if (paymentType && provider?.async_payment_types?.length) {
      if (paymentType === 'OnlineBookingPayment') {
        if (!provider?.booking_fishery_ids?.length) {
          // flag has type overrides defined, but no fishery ID overrides:
          // in this case, we only allow OnlineBookingPayment if explicitly included
          matchesType = provider?.async_payment_types?.includes?.(
            'OnlineBookingPayment'
          );
        }
      } else {
        matchesType = provider?.async_payment_types?.includes?.(paymentType);
      }
    }
    if (provider?.booking_fishery_ids?.length) {
      if (fisheryPublicId) {
        matchesFishery =
          provider?.booking_fishery_ids?.includes?.(+fisheryPublicId);
      } else if (!provider?.async_payment_types?.length) {
        // If flag has fisheryId override and payment types override is empty, require fisheryId to be defined to match.
        // Here there is an ID-only override in place -- so we don't want to match Event payments (for example) since
        // they don't match the supplied ID, however if there is a type-override as well, we need to support matching
        // that type without requiring an ID
        matchesFishery = false;
      }
    }

    return matchesType && matchesFishery;
  };

  let paymentProvider = PaymentProvider.OPAYO; // Fallback for if neither Ryft nor Stripe match
  const stripeMatches = matchesProvider(stripeFlag);
  const ryftMatches = matchesProvider(ryftFlag);

  if (stripeFlag?.all && ryftFlag?.all) {
    // Both have been configured with .all = true -> prefer Stripe
    paymentProvider = PaymentProvider.STRIPE;
  } else if (ryftFlag?.all || !stripeFlag?.all) {
    // Ryft is being as default (or no default is configured) -- treat Stripe flag as override (preferred)
    if (stripeMatches) {
      paymentProvider = PaymentProvider.STRIPE;
    } else if (ryftMatches) {
      paymentProvider = PaymentProvider.RYFT;
    }
  } else if (stripeFlag?.all) {
    // Stripe is being used as default -- treat Ryft flag as override (preferred)
    if (ryftMatches) {
      paymentProvider = PaymentProvider.RYFT;
    } else if (stripeMatches) {
      paymentProvider = PaymentProvider.STRIPE;
    }
  }

  let applePayEnabled = false;
  let storedCardsEnabled = false;
  if (paymentProvider === PaymentProvider.RYFT) {
    applePayEnabled = ryftAplePayEnabled;
    storedCardsEnabled = ryftStoredCardsEnabled;
  }

  // Only allow stored card use for logged-in users
  storedCardsEnabled = storedCardsEnabled && isLoggedIn;

  return { paymentProvider, applePayEnabled, storedCardsEnabled, isLoading };
}

export function usePreloadImage(src) {
  useEffect(() => {
    if (!src) return;
    preloadImage(src);
  }, [src]);
}

/**
 * Returns true when the viewport is at or below `maxWidthPx` (mobile).
 * Defaults to 779px to match the search layout breakpoint (search.css hides the
 * mobile "View Map" button at min-width: 780px). Used to defer mounting the
 * dynamic search map on mobile until the user opens it (a billable Maps load).
 */
export function useIsMobile(maxWidthPx = 779) {
  const query = `(max-width: ${maxWidthPx}px)`;
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, [query]);

  return isMobile;
}

const SLOW_CONNECTION_TYPES = ['slow-2g', '2g', '3g'];

const isLowDataConnection = (connection) =>
  Boolean(connection?.saveData) ||
  SLOW_CONNECTION_TYPES.includes(connection?.effectiveType);

/**
 * Whether the visitor has turned on data saver or is on a slow connection, so
 * optional heavy media such as background video should be skipped. Only
 * Chromium browsers report the connection, so elsewhere it is always false.
 */
export function useIsLowDataConnection() {
  const [isLowData, setIsLowData] = useState(() =>
    isLowDataConnection(navigator.connection)
  );

  useEffect(() => {
    const { connection } = navigator;
    if (!connection) {
      return undefined;
    }
    const handleChange = () => setIsLowData(isLowDataConnection(connection));
    connection.addEventListener('change', handleChange);
    return () => connection.removeEventListener('change', handleChange);
  }, []);

  return isLowData;
}

const IDLE_CALLBACK_TIMEOUT_MS = 2000;
const IDLE_FALLBACK_DELAY_MS = 250;

/**
 * Turns true once the page has loaded and the browser is idle, for work that
 * shouldn't compete with the first paint. Idle callbacks can run before the
 * load event, so it waits for load first. Safari has no requestIdleCallback,
 * so there it waits a short delay after load instead.
 */
export function useIsIdle() {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    let cancelIdleWait = () => {};
    const waitForIdle = () => {
      const markIdle = () => setIsIdle(true);
      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(markIdle, {
          timeout: IDLE_CALLBACK_TIMEOUT_MS,
        });
        cancelIdleWait = () => window.cancelIdleCallback(id);
      } else {
        const id = window.setTimeout(markIdle, IDLE_FALLBACK_DELAY_MS);
        cancelIdleWait = () => window.clearTimeout(id);
      }
    };

    if (document.readyState === 'complete') {
      waitForIdle();
    } else {
      window.addEventListener('load', waitForIdle, { once: true });
    }

    return () => {
      window.removeEventListener('load', waitForIdle);
      cancelIdleWait();
    };
  }, []);

  return isIdle;
}

const SCROLL_TO_HASH_MAX_WAIT_MS = 3000;

/**
 * Scrolls the element matching the URL hash (e.g. `#get-in-touch`) into view.
 * React Router doesn't do this on navigation, and images loading above the
 * target shift it down, so the scroll waits for pending images to settle
 * (capped at SCROLL_TO_HASH_MAX_WAIT_MS).
 * @param block where the target lands in the viewport, as for scrollIntoView
 */
export function useScrollToHash({ block = 'center' } = {}) {
  const { hash } = useLocation();

  useEffect(() => {
    const targetId = hash?.slice(1);
    if (!targetId) {
      return undefined;
    }

    const scrollToTarget = () => {
      document
        .getElementById(targetId)
        ?.scrollIntoView({ behavior: 'smooth', block });
    };

    const pendingImages = [...document.images].filter((img) => !img.complete);
    if (!pendingImages.length) {
      scrollToTarget();
      return undefined;
    }

    let remaining = pendingImages.length;
    let hasScrolled = false;
    const scrollOnce = () => {
      if (!hasScrolled) {
        hasScrolled = true;
        scrollToTarget();
      }
    };
    const handleImageSettled = () => {
      remaining -= 1;
      if (remaining === 0) {
        scrollOnce();
      }
    };

    pendingImages.forEach((img) => {
      img.addEventListener('load', handleImageSettled, { once: true });
      img.addEventListener('error', handleImageSettled, { once: true });
    });
    const fallbackTimeout = setTimeout(scrollOnce, SCROLL_TO_HASH_MAX_WAIT_MS);

    return () => {
      clearTimeout(fallbackTimeout);
      pendingImages.forEach((img) => {
        img.removeEventListener('load', handleImageSettled);
        img.removeEventListener('error', handleImageSettled);
      });
    };
  }, [hash, block]);
}
